import { onIdTokenChanged, signOut, type User } from 'firebase/auth';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth } from '../lib/firebase';
import type { Editor } from '../lib/writes';

type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut'; reason?: 'notAdmin' }
  | { status: 'admin'; user: User; isTest: boolean };

const AuthContext = createContext<AuthState>({ status: 'loading' });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(
    () =>
      onIdTokenChanged(auth, async (user) => {
        if (!user) {
          setState((prev) => (prev.status === 'signedOut' && prev.reason ? prev : { status: 'signedOut' }));
          return;
        }
        const token = await user.getIdTokenResult();
        if (token.claims.admin === true) {
          setState({ status: 'admin', user, isTest: token.claims.isTest === true });
        } else {
          // Customer accounts share the same Firebase project; they never get into the admin.
          setState({ status: 'signedOut', reason: 'notAdmin' });
          await signOut(auth);
        }
      }),
    [],
  );

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/** The signed-in admin. Only call inside routes wrapped by RequireAdmin. */
export function useAdmin(): User {
  const state = useAuth();
  if (state.status !== 'admin') throw new Error('useAdmin() called outside an admin route');
  return state.user;
}

/** Who is writing: stamped on every admin write, and isTest marks test data. */
export function useEditor(): Editor {
  const state = useAuth();
  if (state.status !== 'admin') throw new Error('useEditor() called outside an admin route');
  return { uid: state.user.uid, isTest: state.isTest };
}
